import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import CircleIcon from "@mui/icons-material/Circle";

import { CustomDetailsPage } from "components/DetailsPage/DetailsPage";
import ContentLayout from "components/layouts/ContentLayout";
import { FieldDescriptor } from "models/fieldsEntities.model";
import {
  GiftCardConventional,
  GiftCardTypes,
} from "models/resources/gitfCard.model";

import useGiftCardDetails from "./useGiftCardDetails";

const GiftCardsDetails = () => {
  const { giftCardData, setGiftCardData } = useGiftCardDetails();
  const { t } = useTranslation();
  if (!giftCardData) return <Fragment />;

  let fields: FieldDescriptor[] = [
    {
      value: giftCardData?.name,
      label: "Name",
      type: "InfoText",
      show: true,
    },
    {
      value: giftCardData.company.display_name || giftCardData.company.name,
      id: giftCardData.company_id,
      label: "Company",
      type: "Link",
      route: "companies",
      show: true,
    },
    {
      value: t(giftCardData?.currency),
      label: "Currency",
      type: "InfoText",
      show: true,
    },
    {
      value: t(giftCardData?.type),
      label: "Type",
      type: "InfoText",
      show: true,
    },
    {
      value: Number(giftCardData?.discount_value).toFixed(2),
      label: "Value",
      type: "InfoText",
      show: true,
    },
    {
      value: giftCardData.usage_conditions[0]
        ? `${giftCardData.usage_conditions[0].validity_amount} ${t(
            giftCardData.usage_conditions[0].validity_type,
          )}`
        : "",
      label: "Valid for",
      type: "InfoText",
      show: true,
    },
    {
      value: giftCardData.description,
      label: "Description",
      type: "DescriptiveInfoText",
      show: true,
    },
  ];

  if (giftCardData.type !== "promotional") {
    fields = [
      ...fields,
      {
        value: Number(giftCardData?.price).toFixed(2),
        label: "Purchase price",
        type: "InfoText",
        show: true,
      },
      {
        value: (
          <CircleIcon
            sx={{ color: giftCardData.active ? "#4CAF50" : "#FF6347" }}
          />
        ),
        label: "Active",
        type: "Icon",
        show: true,
      },
    ];
  }

  if (giftCardData.type === GiftCardTypes.Conventional) {
    let purchaseCondition = (giftCardData as GiftCardConventional)
      ?.purchase_conditions?.[0];
    if (purchaseCondition !== undefined) {
      fields = [
        ...fields,
        {
          value: `${
            (giftCardData as GiftCardConventional).purchased_gift_cards_count
          }/${purchaseCondition.total_purchase_amount}`,
          label: "Purchased gift cards",
          type: "InfoText",
          show: true,
        },
      ];
    }
  }

  return (
    <ContentLayout
      title={`${t("Gift Cards")} > ${giftCardData?.name}`}
      filters={false}
    >
      <CustomDetailsPage
        data={giftCardData}
        setData={setGiftCardData}
        resource={"gift-cards"}
        call={"gift-cards"}
        fields={fields}
        isDeletable={!giftCardData.active}
      />
    </ContentLayout>
  );
};

export default GiftCardsDetails;
